import * as ChannelService from '@channel.io/channel-web-sdk-loader'
import { useMutation } from '@tanstack/react-query'
import { useState, useContext, useEffect } from 'react'
import type { ChangeEvent } from 'react'
import DaumPostcode, { Address as DaumAddress } from 'react-daum-postcode'
import styled from 'styled-components'

import { MODALS_ID } from '../../../constants'

import { toast } from '../../../utils'
import { abrCompleteAddressInput } from '../../../utils/abr'

import { StoresContext } from '../../../provider/StoreProvider'
import { ModalContext } from '../../../provider/ModalProvider'

import { useNavigate } from '../../../hooks/useNavigate'
import { useUserInfo } from '../../../hooks/useUserInfo'

import { updateUserInfo } from '../../../api/my'

import { Head } from '../../../components/head/Head'
import { Wrap } from '../../../components/container/Wrap'
import { Content } from '../../../components/container/Content'
import { ColumnItem } from '../../../components/common'
import { ModalWrap } from '../../../components/modal/ModalWrap'
import { Button } from '../../../components/button'
import { Input } from '../../../components/input'
import { Text } from '../../../components/text'
import { Footer } from '../../../components/footer'
import { Header } from '../../../components/header'
import SuccessAddressModal from '../modal/SuccessAddressModal'

interface FullAddress {
  address: string
  addressDetail: string
  zipcode: string
}

const HEAD_INFO = {
  title: '자택주소 | 미술품 조각투자 플랫폼, 아티피오',
  ogTitle: '자택주소 | 미술품 조각투자 플랫폼, 아티피오',
  ogUrl: window.location.href,
}

export function Address() {
  const {
    commonStore: { setInvalidAccess },
  } = useContext(StoresContext)
  const { openModal, closeModal } = useContext(ModalContext)

  const [fullAddress, setFullAddress] = useState<FullAddress>({ address: '', addressDetail: '', zipcode: '' })
  const [isInputted, setIsInputted] = useState<boolean>(false)

  const navigate = useNavigate()
  const { data: userInfo, isLogin, isCheckingLogin, refetch } = useUserInfo()

  const { mutate } = useMutation({
    mutationFn: updateUserInfo,
    onSuccess: async () => {
      await refetch()
      abrCompleteAddressInput()
      ChannelService.track('complete_address_input')

      openModal({
        id: MODALS_ID.SUCCESS_ADDRESS,
        Component: () => (
          <SuccessAddressModal
            onDismiss={() => {
              closeModal(MODALS_ID.SUCCESS_ADDRESS)
              navigate(-1)
            }}
          />
        ),
      })
    },
    onError: (error) => {
      toast('정보 수정에 실패했습니다. 잠시 후 다시 시도해주세요.', error)
    },
  })

  useEffect(() => {
    if (!isLogin && !isCheckingLogin) setInvalidAccess(true)
  }, [isLogin, isCheckingLogin])

  useEffect(() => {
    setFullAddress((prevState) => ({
      ...prevState,
      address: userInfo?.address ?? '',
      addressDetail: userInfo?.addressDetail ?? '',
      zipcode: userInfo?.zipcode ?? '',
    }))
  }, [userInfo])

  const openPostcodeModal = () => {
    openModal({
      id: MODALS_ID.POSTCODE,
      Component: () => (
        <ModalWrap>
          <Header type="MODAL" title="주소 검색" onDismiss={closePostcodeModal} />
          <DaumPostcode
            style={{ height: '100%' }}
            submitMode={false}
            defaultQuery={fullAddress.address || fullAddress.zipcode || undefined}
            onComplete={completeAddress}
          />
        </ModalWrap>
      ),
    })
  }

  const closePostcodeModal = () => closeModal(MODALS_ID.POSTCODE)

  const completeAddress = ({ address, zonecode }: DaumAddress) => {
    setFullAddress((prevState) => ({ ...prevState, address, zipcode: zonecode }))
    setIsInputted(true)
    closePostcodeModal()
  }

  const handleAddress = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    setFullAddress((prevState) => ({ ...prevState, address: value }))
  }

  const handleDetailAddress = ({ target: { value } }: ChangeEvent<HTMLInputElement>) => {
    if (!isInputted) {
      setIsInputted(true)
    }
    setFullAddress((prevState) => ({ ...prevState, addressDetail: typeof value === 'string' ? value : '' }))
  }

  const registerAddress = () => {
    mutate(fullAddress)
  }

  const { address, addressDetail } = fullAddress

  return (
    <>
      <Head {...HEAD_INFO} />
      <Wrap>
        <Header type="SUB" title="자택 주소" />
        <Content>
          <Container $gap={32}>
            <ColumnItem $gap={6}>
              <Text size={24} weight={600} lineHeight={34} block={true}>
                자택 주소를 입력해 주세요.
              </Text>
              <Text size={14} color="text_secondary" lineHeight={22} block={true}>
                청약 시, 필수 정보인 주소를 수집하고 있어요.
              </Text>
            </ColumnItem>
            <ColumnItem $gap={16}>
              <Input.Search
                value={address}
                placeholder="주소/우편번호를 검색해주세요."
                onChange={handleAddress}
                onSearch={openPostcodeModal}
              />
              <Input value={addressDetail} placeholder="상세 주소를 입력해주세요." onChange={handleDetailAddress} />
            </ColumnItem>
          </Container>
        </Content>
        <Footer>
          <Button
            type="primary"
            size="large"
            block={true}
            disabled={!address.trim() || !addressDetail.trim() || !isInputted}
            onClick={registerAddress}
          >
            저장하기
          </Button>
        </Footer>
      </Wrap>
    </>
  )
}

const Container = styled(ColumnItem)`
  padding: 56px 24px 24px;
`
